const SCREENTIME_FUNNEL = {
  slug: 'screentime',
  name: 'Screen Time Funnel',
  analyticsVariant: 'screentime_v1',
  description: 'Canonical Opal screen-time web-to-app funnel.',
  screenOverrides: {},
};

const LAUNCH_2026_SCREEN_OVERRIDES = {
  s0_hero: {
    title: 'PLAN YOUR SOCIAL MEDIA DETOX',
    testimonial: '"I cut my screen time in half. The only app that actually stuck."',
  },
  s1: {
    title: 'What is your daily screen time?',
    kicker: "We'll tailor your detox plan to this",
    entryQuestion: 'screenTimeRange',
    answerField: 'dailyHoursRange',
    key: 'hook_screen_time',
    label: 'Hook+Screen Time',
  },
};

const FAMILIES_FUNNEL = {
  slug: 'families',
  name: 'Families Funnel',
  analyticsVariant: 'families_v1',
  description: 'Real /families funnel: parents quiz, branch, plan preview, two-tier paywall (Solo + Family Sharing up to 5 seats).',
  // 28 screens. f16_family_mechanic removed per May 2026 feedback (subscription
  // mechanic explainer was friction without lift). Household size moved out of
  // f17 into its own onboarding question (f3b). Lifetime projection (shock +
  // hope) added back as f17c/f17d, fed by household-total hours (f17a) and
  // parent age (f17b). Three feature-reveal yes/no screens (Strict Mode,
  // Scheduled Blocks, Gemstone) inserted after f12_reframe to mirror the
  // baseline funnel's curiosity-gap pattern.
  screenOrder: [
    'f1_hook',
    'f2_conflict_window',
    'f2b_conflict_reinforce',
    'f3_family_stage',
    'f3b_household_size',
    'f4_primary_target',
    'f5_app_loop',
    'f6_prior_attempt',
    'f6b_prior_attempt_reinforce',
    'f7_branch_1',
    'f8_branch_2',
    'f9_branch_3',
    'f10_loader',
    'f11_diagnosis',
    'f12_reframe',
    'f12b_strict_mode',
    'f12c_scheduled_blocks',
    'f12d_gemstone',
    'f13_control_style',
    'f14_plan_preview',
    'f15_product_proof',
    'f17a_household_hours',
    'f17b_parent_age',
    'f17_email_gate',
    'f17c_projection_shock',
    'f17d_projection_hope',
    'f18_plan_summary',
    'f19_paywall',
    'f20_handoff',
  ],
  screenOverrides: {
    f1_hook: {
      title: 'Get your family screen-time plan',
      kicker: '1-minute quiz',
    },
  },
};

const LAUNCH_2026_FUNNEL = {
  slug: '2026',
  name: '2026 Launch Funnel',
  analyticsVariant: '2026_v1',
  description: 'Condensed 19-screen 2026 launch funnel using canonical URL-path funnel routing. Adds s15c (habits multi-select) + s15d (usage plan) between sacrifice reaffirmation and the projection loader so the "Crunching your numbers" beat can feel personalised.',
  revenueCatOffering: 'Web: Yearly, Monthly, Weekly with 3dayTrial',
  // Display order for paywall cards — short → long. Note the visual-middle
  // preselection in screens-phase8.jsx will now anchor MOST POPULAR + the
  // default selection on the MONTHLY plan (index 1 of 3). If we want Annual
  // to keep MOST POPULAR, the anchor logic in screens-phase8.jsx needs to
  // change (e.g. pin to a specific identifier rather than the middle index).
  paywallPackageOrder: ['$rc_weekly', '$rc_monthly', '$rc_annual'],
  paywallTrialCtaLabel: 'Start 3 days for $0.00',
  screenOrder: [
    's0_hero',
    's1', 's4', 's7_age', 's15', 's15b', 's15c_habits', 's15d_use_plan',
    's8', 's9', 's10',
    's14', 's26', 's32', 's32b', 's46', 's47', 's49', 's53',
  ],
  screenOverrides: LAUNCH_2026_SCREEN_OVERRIDES,
};

const SHORT_2026_FUNNEL = {
  slug: '2026-short',
  name: '2026 Short Funnel',
  analyticsVariant: '2026_short_v1',
  description: 'Draft 10-screen /2026 variant: about 50% shorter, using only existing launch funnel screens.',
  revenueCatOffering: LAUNCH_2026_FUNNEL.revenueCatOffering,
  paywallPackageOrder: LAUNCH_2026_FUNNEL.paywallPackageOrder,
  paywallTrialCtaLabel: LAUNCH_2026_FUNNEL.paywallTrialCtaLabel,
  screenOrder: [
    's0_hero',
    's1',
    's4',
    's7_age',
    's8',
    's9',
    's10',
    's47',
    's49',
    's53',
  ],
  screenOverrides: LAUNCH_2026_SCREEN_OVERRIDES,
};

const DOWNLOAD_FUNNEL = {
  slug: 'download',
  name: 'Download Funnel',
  analyticsVariant: '2026_short_download_v1',
  description: 'Download version of the existing /2026-short funnel that skips RevenueCat purchase and hands off to the Adjust download link.',
  screenOrder: [
    's0_hero',
    's1',
    's4',
    's7_age',
    's8',
    's9',
    's10',
    's47',
    's53_download',
  ],
  screenOverrides: LAUNCH_2026_SCREEN_OVERRIDES,
};

// /2026-start — paywall acquisition funnel for the start.opalapp.com static
// landing pages (public/landing/*.html). Same quiz flow as /2026-short, ending
// in the 3-day-trial paywall (s49) + handoff (s53) on the /2026 RevenueCat
// offering. It is a SEPARATE slug + analytics variant so the two paywall
// acquisition surfaces can be told apart in Amplitude on `funnel_slug` alone:
// the opalapp.com Webflow site stays on /2026-short, while the start.opalapp.com
// static landing points here. (Distinct from the `download` funnel, which is the
// no-paywall Adjust-download surface.) Named 2026-start — not 2026-get — to avoid
// confusion with the separate /get App Store landing funnel.
//
// 2026-start-only content lives behind slug checks: granular age buckets in
// screens-phase2 (AGE_OPTIONS_START) and the Facebook "moments that matter" s4
// quote in screens-phase1. The gated screens it uses (s0_hero, s7_age) list
// '2026-start' alongside the baseline funnels.
const START_2026_FUNNEL = {
  slug: '2026-start',
  name: '2026 Start Funnel (start.opalapp.com surface)',
  analyticsVariant: '2026_start_v1',
  description: 'Paywall acquisition funnel served from the start.opalapp.com static landing pages. Same flow as /2026-short (3-day-trial paywall s49 + handoff s53, /2026 RevenueCat offering); separate slug/variant for acquisition-surface attribution vs the opalapp.com website, which stays on /2026-short.',
  revenueCatOffering: LAUNCH_2026_FUNNEL.revenueCatOffering,
  paywallPackageOrder: LAUNCH_2026_FUNNEL.paywallPackageOrder,
  paywallTrialCtaLabel: LAUNCH_2026_FUNNEL.paywallTrialCtaLabel,
  // Paywall V3 (paywall-v3.js) — 100% of /2026-start: a value screen, then a
  // Yearly / Monthly picker. Replaces the paid_trial_v1 surface.
  // `?paywall=v2` restores the fields above (3-day-trial offering, V2 layout,
  // paid_trial_v1).
  //
  // Offering: the shared 7-day-trial offering (also /detox and /get), now
  // $rc_annual → prod_yearly_pro_99_7d_trial ($99.99/yr) and
  // $rc_monthly → prod_monthly_pro_19_7d_trial ($19.99/mo).
  paywallV3: {
    revenueCatOffering: 'Web: Yearly, Monthly, Weekly with 7dayTrial',
    paywallPackageOrder: ['$rc_annual', '$rc_monthly'],
    paywallPackagesExclusive: true,
    paywallPopularPackageId: '$rc_annual',
    paywallTrialCtaLabel: 'Start My Free Trial',
  },
  screenOrder: [
    's0_hero',
    's1',
    's4',
    's7_age',
    's8',
    's9',
    's10',
    's47',
    's49',
    's53',
  ],
  screenOverrides: LAUNCH_2026_SCREEN_OVERRIDES,
};

// /faith-christian — Christian-targeted copy of the /2026-start acquisition
// funnel. Same screen-time → shock → hope → paywall spine, reframed for an
// audience whose motivation to cut screen time is to get closer to God (more
// prayer, scripture, family, rest). Served from its own start.opalapp.com
// landing page (public/landing/opalapp-faith-christian.html) and kept on its
// own slug/variant so the religious surfaces are cleanly separable in
// Amplitude on `funnel_slug` alone. Adds one screen vs /2026-start —
// faith_blockscreen — which shows Opal's Christian block screen as the proof
// beat (gated funnelOnly: ['faith-christian'] in screens-phase2.jsx).
//
// Pricing is UNCHANGED: it reuses the /2026 3-day-trial offering. No invented
// discounts or comparators.
const FAITH_CHRISTIAN_FUNNEL = {
  slug: 'faith-christian',
  name: 'Faith Funnel — Christian (start.opalapp.com surface)',
  analyticsVariant: 'faith_christian_v1',
  description: 'Christian-targeted copy of /2026-start: same screen-time → shock → hope → 3-day-trial paywall spine, reframed for getting closer to God, plus a faith_blockscreen proof beat showing Opal\'s Christian block screen. Separate slug/variant for acquisition-surface attribution.',
  revenueCatOffering: LAUNCH_2026_FUNNEL.revenueCatOffering,
  // Paywall shows only Monthly + Annual — the weekly product in the shared
  // /2026 3-day-trial offering is dropped via paywallPackagesExclusive (the
  // order alone only sorts; exclusive filters). Pricing itself is unchanged.
  // Annual is listed first and pinned as MOST POPULAR / default selection via
  // paywallPopularPackageId (otherwise the middle-index anchor would flip the
  // ribbon + preselection to Monthly once Annual moved to the top).
  paywallPackageOrder: ['$rc_annual', '$rc_monthly'],
  paywallPackagesExclusive: true,
  paywallPopularPackageId: '$rc_annual',
  paywallTrialCtaLabel: LAUNCH_2026_FUNNEL.paywallTrialCtaLabel,
  screenOrder: [
    's0_hero',
    's1',
    's4',
    's7_age',
    's8',
    's9',
    's10',
    'faith_blockscreen',
    's47',
    's49',
    's53',
  ],
  screenOverrides: {
    ...LAUNCH_2026_SCREEN_OVERRIDES,
    s0_hero: {
      // Mirrors the base "PLAN YOUR SOCIAL MEDIA DETOX" imperative cadence,
      // reframed around the faith motivation (getting closer to God).
      title: 'PLAN YOUR DETOX FOR A LIFE WITH GOD',
      testimonial: '"I gave the hours I lost scrolling back to prayer and my family. Opal made it stick."',
    },
    s4: {
      headline: 'Join Christians reclaiming their time for God',
    },
  },
};

// JUN-62 / JUN-87 / JUN-101 / JUN-108: real /adhd funnel.
//
// JUN-87 round expanded the flow from 19 to 24 screens (s4 social proof,
// three slider-reinforcement beats a5b/a6b/a7b, canonical s9/s10 projection).
// JUN-101 added the rage section (a10a thesis + a10b betrayal + a11 koan).
// JUN-108 tightens the flow back down:
//   - drops the three slider reinforcements (a5b/a6b/a7b) — review feedback
//     called them out as redundant with similar photography between them.
//   - drops a12_diagnosis — redundant after the rage section already delivers
//     the same beat with more force.
// Net: 20 screens (was 26).
//
// Hero pattern mirrors /2026: s1 with entryQuestion='screenTimeRange' writes
// both `dailyHoursRange` and `dailyHours` so s9 + s10 can render their
// projections without a separate hours screen. Age is not collected in /adhd
// — calcScreenYears falls back to 50-year remaining.
const ADHD_FUNNEL = {
  slug: 'adhd',
  name: 'ADHD Funnel',
  analyticsVariant: 'adhd_v1',
  description: 'Real /adhd funnel: 22 screens (s1 entry, s4 social proof, a3..a18 ADHD-specific, canonical s9+s10 projection, s49 paywall, s53 handoff).',
  revenueCatOffering: 'Web: ADHD Offering',
  paywallPackageOrder: ['$rc_weekly', '$rc_monthly', '$rc_annual'],
  paywallTrialCtaLabel: 'Start 3 days for $0.00',
  // s9/s10 read sacrifice via projection-copy when an SACRIFICE_COPY override
  // is present. Pre-set so any state that lands on s9 with no prior sacrifice
  // pick still renders the ADHD-flavoured 'attention' copy.
  defaults: { sacrifice: 'attention' },
  screenOrder: [
    's1',
    's4',                         // social-proof beat (JUN-87 #6)
    'a3_goal',
    'a4_goal_reinforce',
    'a5_severity_focus',
    'a6_severity_impulse',
    'a7_severity_overwhelm',
    'a8_when',
    'a9_apps',
    'a10_tried',
    'a10a_thesis',                // JUN-101: "Social media causes ADHD" + JAMA pill
    'a10b_betrayal',              // JUN-101: per-app personalised callouts (reads state.appPicks)
    'a11_not_your_fault',         // JUN-108: restored to reinforcement-card pattern
    'a13_email_gate',
    'a14_loader',
    's9',                         // canonical years-of-life shock (JUN-87 #5)
    's10',                        // canonical years-of-life hope (JUN-87 #5)
    'a16_fact_23min',
    'a17_plan_reveal',
    'a18_testimonials',
    's49',
    's53',
  ],
  screenOverrides: {
    s1: {
      title: 'See what ADHD is costing you',
      kicker: '2-min quiz · based on your screen time',
      entryQuestion: 'screenTimeRange',
      answerField: 'dailyHoursRange',
      key: 'hook_screen_time',
      label: 'Hook+Screen Time',
    },
    s4: {
      headline: '1 million people with ADHD brains regaining focus',
      subhead: 'They use Opal to keep distracting apps from hijacking their day.',
    },
  },
};

// /detox — Social Media Detox Plan funnel. Builds on the 2026 funnel's
// elements (s0_hero hero, s1 screen-time entry, s7_age, s15* sacrifice flow,
// s8 loader, s9 shock, s10 hope, etc.) and adds the detox-specific screens:
// s7_time (time-of-day capture), s10_ack (Paladin-style acknowledgement
// beat), s45_summary (pre-plan card list), s48_preview
// (day-timeline + 12-week ramp), and the relocated s4_pre_paywall trust beat.
//
// Screens with `variant: 'detox_v1'` only ever render on this funnel — see
// filterByDetoxVariant in app.jsx. Conversely, the original-position s4 social
// proof (`variant: 'pre_detox'`) drops out of this funnel so /detox doesn't
// duplicate the trust beat.
//
// The s1 hero override mirrors /2026's screen-time entry but tightens the
// kicker on the detox framing.
const LAUNCH_DETOX_FUNNEL = {
  slug: 'detox',
  name: 'Social Media Detox Plan Funnel',
  analyticsVariant: 'detox_v1',
  description: 'Detox Plan funnel: inherits the 2026 element pool and adds tier classification, time-of-day capture, three Paladin-style acknowledgement beats, a pre-plan summary card list, a day-timeline sneak peek, and the s4 social proof relocated to right before the paywall.',
  // /detox-specific offering: 2 packages only (monthly + annual), both with a
  // 7-day free trial. The "Weekly" in the RC offering name is historical —
  // the actual offering has no weekly product.
  revenueCatOffering: 'Web: Yearly, Monthly, Weekly with 7dayTrial',
  paywallPackageOrder: ['$rc_monthly', '$rc_annual'],
  paywallTrialCtaLabel: 'Start 7 days for $0.00',
  screenOrder: [
    's0_hero',
    's1',
    's7_age', 's15', 's15b', 's15c_habits', 's15d_use_plan',
    's7', 's7_time',
    's8', 's9', 's10', 's10_ack',
    's14',
    's27_urge_fact', 's32',
    's45_summary', 's46', 's47',
    's48_preview',
    's4_pre_paywall',
    's49', 's53',
  ],
  screenOverrides: {
    s0_hero: {
      title: 'YOUR SOCIAL MEDIA DETOX PLAN',
      testimonial: '"Find out which tier you\'re in — and what to do about it."',
    },
    s1: {
      title: 'What is your daily screen time?',
      kicker: "We'll build your detox plan around it",
      entryQuestion: 'screenTimeRange',
      answerField: 'dailyHoursRange',
      key: 'hook_screen_time',
      label: 'Hook+Screen Time',
    },
    // On /detox, s32 renders the interactive practice (the lead-in is merged
    // in) — keep the analytics headline matching what's shown, since the
    // screen's base headline is the baseline funnels' yes/no question.
    s32: {
      headline: "Let's see Opal in action.",
      label: '5-sec wait practice',
    },
    // s14 shows the reframed "Engineers build feeds…" lead on /detox; the
    // screen's base headline is the baseline "Willpower fails." beat.
    s14: {
      headline: 'Engineers build feeds to trap your attention. You need hard limits to stop them.',
    },
  },
};

// /get — App Store-style landing page that opens straight into a light-themed
// 7-day-trial paywall. Three screens, all defined in screens-phase1.jsx and
// gated funnelOnly:['get']:
//   get_landing  — App Store screenshots stacked vertically + "Try for free"
//   get_paywall  — light-blue paywall on the 7-day-trial RevenueCat offering
//   get_handoff  — post-purchase install + redeem (light palette)
// Reuses the detox funnel's 7-day-trial offering (monthly + annual, both with
// a 7-day free trial) — same offering shipped in prod for /detox.
const GET_LANDING_FUNNEL = {
  slug: 'get',
  name: 'Get Landing Page',
  analyticsVariant: 'get_v1',
  description: 'App Store landing page → light-themed 7-day-trial paywall → handoff. Uses the Web 7dayTrial RevenueCat offering (monthly + annual).',
  revenueCatOffering: 'Web: Yearly, Monthly, Weekly with 7dayTrial',
  paywallPackageOrder: ['$rc_monthly', '$rc_annual'],
  paywallTrialCtaLabel: 'Start 7 days for $0.00',
  screenOrder: ['get_landing', 'get_paywall', 'get_handoff'],
  screenOverrides: {},
};

// /schools-promo — Opal for Schools, parents offer.
//
// A standalone one-pager, deliberately sharing no screens with any other
// funnel: sales sends parents at school customers a direct link, they see one
// offer, they claim it. Both screens live in screens-schools.jsx, gated
// funnelOnly: ['schools-promo'], and app.jsx loads that file as the funnel's
// only chunk.
//
// Pricing comes from the RevenueCat offering "Web Schools Parents Offer": one
// package, $rc_annual -> Yearly35_NoTrial, $35/year with no free trial. If the
// identifier ever stops matching, the paywall renders its load-error state
// rather than falling back to a full-price offering.
const SCHOOLS_PROMO_FUNNEL = {
  slug: 'schools-promo',
  name: 'Opal for Schools — Parents Offer',
  analyticsVariant: 'schools_promo_v1',
  description: 'Standalone one-pager for the discounted school-partner rate sales offers to parents at school customers. Two dedicated screens (schools_paywall + schools_handoff), no quiz, no landing page, no screens shared with other funnels.',
  revenueCatOffering: 'Web Schools Parents Offer',
  screenOrder: ['schools_paywall', 'schools_handoff'],
  screenOverrides: {},
};

const FUNNELS = {
  screentime: SCREENTIME_FUNNEL,
  families: FAMILIES_FUNNEL,
  '2026': LAUNCH_2026_FUNNEL,
  '2026-short': SHORT_2026_FUNNEL,
  download: DOWNLOAD_FUNNEL,
  '2026-start': START_2026_FUNNEL,
  'faith-christian': FAITH_CHRISTIAN_FUNNEL,
  adhd: ADHD_FUNNEL,
  detox: LAUNCH_DETOX_FUNNEL,
  get: GET_LANDING_FUNNEL,
  'schools-promo': SCHOOLS_PROMO_FUNNEL,
};

const RESERVED_PATH_SEGMENTS = new Set([
  '',
  'assets',
  'api',
  'favicon.ico',
  'privacy',
  'terms',
  // /dev/* is used for non-production tooling routes (e.g. /dev/ab-test, the
  // A/B testing smoke validation page). They short-circuit the funnel render
  // in app.jsx — keep them out of funnel resolution for safety.
  'dev',
]);

function normalizeSlug(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^[#/]+|[/#]+$/g, '')
    .replace(/[^a-z0-9-]+/g, '-');
}

function slugFromPath(pathname = '/') {
  const first = normalizeSlug(String(pathname).split('?')[0].split('/').filter(Boolean)[0] || '');
  return RESERVED_PATH_SEGMENTS.has(first) ? '' : first;
}

function slugFromHostname(hostname = '') {
  const host = String(hostname).toLowerCase().split(':')[0];
  const parts = host.split('.').filter(Boolean);
  if (parts.length < 4) return '';

  // Supports screentime.funnel.opal.so while avoiding the base funnel.opal.so host.
  const [candidate, second] = parts;
  if (second !== 'funnel') return '';
  return normalizeSlug(candidate);
}

export function getFunnelBySlug(slug) {
  return FUNNELS[normalizeSlug(slug)] || SCREENTIME_FUNNEL;
}

export function resolveFunnelFromUrl({ pathname = '/', hostname = '' } = {}) {
  const pathSlug = slugFromPath(pathname);
  const hostSlug = slugFromHostname(hostname);
  return getFunnelBySlug(pathSlug || hostSlug || 'screentime');
}

export function resolveCurrentFunnel() {
  if (typeof window === 'undefined') return SCREENTIME_FUNNEL;
  return resolveFunnelFromUrl({
    pathname: window.location?.pathname || '/',
    hostname: window.location?.hostname || '',
  });
}

export function applyFunnelToScreen(screen, funnel = SCREENTIME_FUNNEL) {
  if (!screen || !funnel) return screen;
  const override = funnel.screenOverrides?.[screen.id];
  if (!override) return screen;
  // Resolve the analytical headline against the override copy so funnel-specific
  // copy (e.g. families funnel's s1.title) is what flows into screen_headline.
  // `headlineFromFunnelCopy` names the override field that drives the headline;
  // when present it wins, otherwise we accept a direct `title`/`headline` override.
  const overriddenHeadline = (() => {
    if (screen.headlineFromFunnelCopy && override[screen.headlineFromFunnelCopy] != null) {
      return override[screen.headlineFromFunnelCopy];
    }
    if (override.headline != null) return override.headline;
    if (override.title != null) return override.title;
    return null;
  })();
  const screenMetadataOverrides = {};
  for (const key of ['answerField', 'key', 'label']) {
    if (override[key] != null) screenMetadataOverrides[key] = override[key];
  }
  return {
    ...screen,
    ...screenMetadataOverrides,
    headline: overriddenHeadline != null ? overriddenHeadline : screen.headline,
    funnelCopy: {
      ...(screen.funnelCopy || {}),
      ...override,
    },
  };
}

function screenAllowedForFunnel(screen, funnel = SCREENTIME_FUNNEL, variantResolver) {
  if (!screen) return false;
  if (Array.isArray(screen.funnelOnly) && screen.funnelOnly.length > 0) {
    if (!screen.funnelOnly.map(normalizeSlug).includes(normalizeSlug(funnel.slug))) return false;
  }
  // `requiredVariant: { flag, value }` gates a whole screen behind an A/B
  // assignment. Defaults to EXCLUDED when the resolver is absent or returns
  // a non-matching value — so a user whose experiment SDK hasn't resolved
  // yet falls back to control (no hero shown), not to a flash of treatment.
  // Callers re-run `applyFunnelToScreens` once the SDK becomes ready (see
  // app.jsx) to honor the actual assignment.
  if (screen.requiredVariant && typeof screen.requiredVariant === 'object') {
    const { flag, value } = screen.requiredVariant;
    if (!flag) return true; // misconfigured — fail open
    const resolved = typeof variantResolver === 'function' ? variantResolver(flag) : undefined;
    if (resolved !== value) return false;
  }
  // `enabled: () => boolean` is the synchronous escape-hatch gate used by
  // screens that have been rolled out to 100% but still expose a
  // `?<flag>=off` query / sessionStorage override for QA + support. Returning
  // false excludes the screen from the funnel; missing/undefined means
  // allow (so most screens are unaffected).
  if (typeof screen.enabled === 'function') {
    try {
      if (screen.enabled() === false) return false;
    } catch {
      // a buggy enabled() must not crash the whole funnel — fail open.
    }
  }
  return true;
}

export function applyFunnelToScreens(screens, funnel = SCREENTIME_FUNNEL, options = {}) {
  const { variantResolver } = options;
  const resolved = (screens || [])
    .map((screen) => applyFunnelToScreen(screen, funnel))
    .filter((screen) => screenAllowedForFunnel(screen, funnel, variantResolver));
  if (!Array.isArray(funnel?.screenOrder) || funnel.screenOrder.length === 0) return resolved;

  const byId = new Map(resolved.map((screen) => [screen.id, screen]));
  return funnel.screenOrder.map((id) => byId.get(id)).filter(Boolean);
}

export { FUNNELS, SCREENTIME_FUNNEL, FAMILIES_FUNNEL, LAUNCH_2026_FUNNEL, SHORT_2026_FUNNEL, DOWNLOAD_FUNNEL, START_2026_FUNNEL, FAITH_CHRISTIAN_FUNNEL, ADHD_FUNNEL, LAUNCH_DETOX_FUNNEL, GET_LANDING_FUNNEL, SCHOOLS_PROMO_FUNNEL };
